/**
 * <sky-lookbook> — pinned scroll carousel. The current look stands large on the right;
 * earlier looks shrink, fade and blur towards the left, and the product column scrolls in sync.
 */
const BASE_WIDTH = 356;
const SHRINK = 54; // px lost per step back
const GAP = 10;
const OPACITY = [1, 0.53, 0.33, 0.3];
const BLUR = [0, 0.67, 2.33, 3.5];

/** @param {number[]} table @param {number} k */
function lerpTable(table, k) {
  const i = Math.min(Math.floor(k), table.length - 1);
  const next = table[Math.min(i + 1, table.length - 1)];
  return table[i] + (next - table[i]) * Math.min(k - i, 1);
}

class SkyLookbook extends HTMLElement {
  connectedCallback() {
    this.models = [...this.querySelectorAll('[data-model]')];
    this.products = this.querySelector('[data-products]');
    this.left = this.querySelector('.sky-lookbook__left');

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.render();
      });
    };
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    cancelAnimationFrame(this.frame);
  }

  render() {
    if (!this.left || this.offsetParent === null) return;

    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;
    const distance = Math.max(rect.height - vh, 1);
    const progress = Math.min(Math.max(-rect.top / distance, 0), 1);
    const steps = Math.max(this.models.length - 1, 0);
    const position = progress * steps;

    const right = this.left.clientWidth;
    const scale = Math.min(1, (vh * 0.6) / (BASE_WIDTH * 1.5));

    this.models.forEach((model, i) => {
      const d = i - position;
      let left;
      let width;

      if (d >= 0) {
        width = BASE_WIDTH;
        left = right - BASE_WIDTH * scale + d * (BASE_WIDTH + GAP) * scale;
      } else {
        const k = -d;
        width = Math.max(BASE_WIDTH - SHRINK * k, 120);
        const back = (BASE_WIDTH + GAP) * k - (SHRINK / 2) * k * (k + 1);
        left = right - BASE_WIDTH * scale - back * scale;
      }

      const k = Math.max(-d, 0);
      model.style.transform = `translate(${left.toFixed(1)}px, -50%) scale(${((width / BASE_WIDTH) * scale).toFixed(4)})`;
      model.style.opacity = String(lerpTable(OPACITY, k));
      model.style.filter = k > 0.01 ? `blur(${lerpTable(BLUR, k).toFixed(2)}px)` : 'none';
      model.style.zIndex = String(100 - Math.round(k * 10));
    });

    if (this.products) {
      const card = this.products.firstElementChild;
      const step = card ? card.getBoundingClientRect().height + 20 : 0;
      const max = Math.max(this.products.scrollHeight - vh, 0);
      this.products.style.setProperty('--products-y', `${-Math.min(position * step, max)}px`);
    }
  }
}

if (!customElements.get('sky-lookbook')) customElements.define('sky-lookbook', SkyLookbook);
